import { useState } from 'react'
import { Eye, Download } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'

type Props = {
  url: string
  filename?: string
  trigger?: React.ReactNode
}

export function DocumentPreviewModal({ url, filename = 'Document', trigger }: Props) {
  const [open, setOpen] = useState(false)
  const isImage = url.match(/\.(jpeg|jpg|gif|png|webp|svg|bmp)(\?.*)?$/i)
  const isPdf = url.match(/\.pdf(\?.*)?$/i)
  
  const filenameIsImage = filename.match(/\.(jpeg|jpg|gif|png|webp|svg|bmp)$/i)
  const filenameIsPdf = filename.match(/\.pdf$/i)

  const canPreview = isImage || isPdf || filenameIsImage || filenameIsPdf

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground" title="Preview">
            <Eye className="w-3.5 h-3.5" />
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-[95vw] md:max-w-4xl w-full h-[90vh] md:h-[80vh] flex flex-col p-0 gap-0 overflow-hidden sm:max-w-[95vw]">
        <DialogHeader className="px-4 py-3 border-b flex-row justify-between items-center space-y-0 bg-background z-10 shrink-0">
          <DialogTitle className="text-sm font-medium truncate pr-4">{filename}</DialogTitle>
          <div className="flex items-center gap-2 pr-6">
            <Button asChild variant="outline" size="sm" className="h-8">
              <a href={url} target="_blank" rel="noopener noreferrer" download>
                <Download className="w-3.5 h-3.5 mr-2" />
                Download
              </a>
            </Button>
          </div>
        </DialogHeader>
        <div className="flex-1 bg-muted/30 overflow-auto relative flex items-center justify-center p-4">
          {canPreview ? (
            (isImage || filenameIsImage) ? (
              <img src={url} alt={filename} className="max-w-full max-h-full object-contain shadow-sm rounded-md bg-white" />
            ) : (
              <iframe 
                src={url} 
                className="w-full h-full bg-white border-0 shadow-sm rounded-md" 
                title={filename}
              />
            )
          ) : (
            <div className="text-center">
              <p className="text-sm text-muted-foreground mb-4">Preview not available for this file type.</p>
              <Button asChild>
                <a href={url} target="_blank" rel="noopener noreferrer" download>
                  Download File
                </a>
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
